.page {
  display: grid;
  grid-template-columns: 1fr minmax(0, 520px);
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--bg-canvas);
}

/* ---- Left: the brand panel ---- */
.aside {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: var(--space-12);
  overflow: hidden;
  background:
    radial-gradient(900px 600px at 15% 10%, var(--accent-subtle), transparent 60%),
    radial-gradient(700px 500px at 80% 85%, var(--signal-subtle), transparent 60%),
    var(--bg-surface-sunken);
  border-right: 1px solid var(--border-subtle);
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  position: relative;
}

.mark {
  object-fit: contain;
  width: 38px;
  height: 38px;
  filter: invert(1);
}

:global(:root[data-theme='light']) .mark {
  filter: none;
}

.wordmark {
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
  letter-spacing: -0.02em;
}

.wordmarkMuted {
  color: var(--text-tertiary);
  font-weight: var(--weight-semibold);
}

.pitch {
  position: relative;
  z-index: 1;
  max-width: 44ch;
}

.pitchTitle {
  font-size: var(--text-3xl);
  letter-spacing: -0.03em;
  line-height: 1.12;
}

.pitchBody {
  margin-top: var(--space-4);
  color: var(--text-secondary);
  font-size: var(--text-md);
  line-height: var(--leading-relaxed);
}

.points {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  list-style: none;
  padding: 0;
  margin: var(--space-8) 0 0;
}

.point {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--text-sm);
  color: var(--text-secondary);
}

.pointIcon {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-sm);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  color: var(--accent);
  flex-shrink: 0;
}

.asideFooter {
  position: relative;
  z-index: 1;
  font-size: var(--text-2xs);
  color: var(--text-tertiary);
  font-family: var(--font-mono);
}

/* The decorative EEG trace. Anchored to the bottom edge and faded out at both
   ends, so it reads as a footer motif rather than a line struck through the
   feature list. */
.trace {
  position: absolute;
  inset: auto 0 0 0;
  width: 100%;
  height: 150px;
  color: var(--accent);
  opacity: 0.2;
  pointer-events: none;
  -webkit-mask-image: linear-gradient(to bottom, transparent, #000 55%),
    linear-gradient(to right, transparent, #000 12%, #000 88%, transparent);
  -webkit-mask-composite: source-in;
  mask-image: linear-gradient(to bottom, transparent, #000 55%),
    linear-gradient(to right, transparent, #000 12%, #000 88%, transparent);
  mask-composite: intersect;
}

/* ---- Right: the form ---- */
.formPane {
  display: grid;
  place-items: center;
  padding: var(--space-8);
  background: var(--bg-surface);
}

.form {
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.formHead {
  margin-bottom: var(--space-1);
}

.formTitle {
  font-size: var(--text-xl);
  letter-spacing: -0.02em;
}

.formSubtitle {
  margin-top: var(--space-2);
  color: var(--text-secondary);
}

.alert {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  animation: ela-fade-in var(--duration-normal) var(--ease-out);
}

.alertIcon {
  flex-shrink: 0;
  margin-top: 2px;
}

.alertNetwork {
  background: var(--warning-subtle);
  border-color: var(--warning);
  color: var(--text-primary);
}

.alertNetwork .alertIcon {
  color: var(--warning);
}

.alertAuth {
  background: var(--danger-subtle);
  border-color: var(--danger);
  color: var(--text-primary);
}

.alertAuth .alertIcon {
  color: var(--danger);
}

.alertValidation {
  background: var(--warning-subtle);
  border-color: var(--warning);
  color: var(--text-primary);
}

.alertValidation .alertIcon {
  color: var(--warning);
}

.alertUnknown {
  background: var(--bg-inset);
  border-color: var(--border-default);
  color: var(--text-secondary);
}

.divider {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  color: var(--text-tertiary);
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-subtle);
}

.providers {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.providerButton :global(.label) {
  gap: var(--space-3);
}

.providerIcon {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
}

.providerButton:last-child .providerIcon {
  filter: brightness(0);
}

:global(:root[data-theme='dark']) .providerButton:last-child .providerIcon {
  filter: brightness(0) invert(1);
}

.note {
  font-size: var(--text-xs);
  color: var(--text-tertiary);
  line-height: var(--leading-relaxed);
  text-align: center;
}

.themeToggle {
  position: absolute;
  top: var(--space-5);
  right: var(--space-5);
}

/* ---- Responsive: the brand panel steps aside on tablet and below ---- */
@media (max-width: 960px) {
  .page {
    grid-template-columns: minmax(0, 1fr);
  }

  .aside {
    display: none;
  }

  .formPane {
    background: var(--bg-canvas);
    align-content: center;
    padding: var(--space-6) var(--space-5);
  }

  .form {
    max-width: 420px;
  }
}
